import { AgeCategoryEnum } from '@msc-b2c/interfaces/enums';
import {
  GetShopExcursionMyMSCResponse,
  OBSI,
  PassengerI,
  ShopExcursionDiscounDetailsI,
  ShopExcursionPassengerI,
  ShopOBSPassengerI,
} from '@msc-b2c/interfaces/interfaces';
import { getAvailablePrice, getBeforePromotionPrice, roundPrice } from '@msc-b2c/shared-utils';

/**
 * Sets all passengers to the specified checked state.
 *
 * @param passengers - Array of passengers to set the checked state for.
 * @param checked - Boolean indicating whether to check or uncheck all passengers.
 * @returns An object mapping passenger IDs to their checked state.
 */
export function setAllPassengers(
  passengers: PassengerI[],
  checked: boolean
): Record<string, boolean> {
  return Object.fromEntries(passengers.map((passenger) => [passenger.id, checked]));
}

/**
 * Sets all passengers in a cabin to the specified checked state.
 *
 * @param passengers - Record of passenger IDs and their checked state.
 * @param checked - Boolean indicating whether to check or uncheck all passengers in the cabin.
 * @returns An object mapping passenger IDs to their checked state in the cabin.
 */
export function setAllPassengersOnCabin(
  passengers: Record<string, boolean>,
  checked: boolean
): Record<string, boolean> {
  return Object.fromEntries(Object.keys(passengers).map((passengerId) => [passengerId, checked]));
}

/**
 * Checks if at least one adult or senior passenger is selected.
 *
 * @param passengers - Record of passenger IDs and their checked state.
 * @param cabinPassengers - Array of passengers in the cabin.
 * @param isAdultOrSeniorAlreadyBooked - Boolean indicating if an adult or senior is already booked.
 * @returns True if at least one adult or senior is selected, false otherwise.
 */
export function atLeastOneAdultSelected(
  passengers: Record<string, boolean>,
  cabinPassengers: PassengerI[],
  isAdultOrSeniorAlreadyBooked: boolean
): boolean {
  return (
    isAdultOrSeniorAlreadyBooked ||
    Object.entries(passengers).some(([passengerId, isChecked]) => {
      const passenger = cabinPassengers.find((p) => p.id === passengerId);

      return isChecked && isPassengerAdultOrSenior(passenger);
    })
  );
}

/**
 * Checks if a passenger is an adult or senior.
 *
 * @param passenger - The passenger to check.
 * @returns True if the passenger is an adult or senior, false otherwise.
 */
export function isPassengerAdultOrSenior(passenger: PassengerI | undefined): boolean {
  if (!passenger) {
    return false;
  }

  return (
    passenger.ageCategory === AgeCategoryEnum.ADULT ||
    passenger.ageCategory === AgeCategoryEnum.SENIOR
  );
}

export function getLowestApplicablePrice(
  prices: ShopExcursionPassengerI[] | ShopOBSPassengerI[] | undefined
): ShopExcursionDiscounDetailsI | null {
  // Return default values for empty input cases
  if (!prices || prices.length === 0) {
    return null;
  }

  const applicablePrices = prices.filter((price) => price.isApplicable);
  if (applicablePrices.length === 0) {
    return null;
  }

  const hasAdult = applicablePrices.some(
    (price) =>
      price.ageCategory === AgeCategoryEnum.ADULT || price.ageCategory === AgeCategoryEnum.SENIOR
  );
  const filteredPrices = hasAdult
    ? applicablePrices.filter(
        (price) =>
          price.ageCategory === AgeCategoryEnum.ADULT ||
          price.ageCategory === AgeCategoryEnum.SENIOR
      )
    : applicablePrices;

  if (filteredPrices.length === 0) {
    return null;
  }

  const lowestPrice = filteredPrices.reduce((lowest, current) => {
    const currentAvailablePrice = getAvailablePrice(current.total);
    const lowestAvailablePrice = getAvailablePrice(lowest.total);
    const currentAfterDiscount = parseFloat(currentAvailablePrice ?? '0');
    const lowestAfterDiscount = parseFloat(lowestAvailablePrice ?? '0');
    return currentAfterDiscount < lowestAfterDiscount ? current : lowest;
  }, filteredPrices[0]);

  // Format the price values
  const beforePromotionAndDiscount = getFormattedPrice(
    getBeforePromotionPrice({
      afterDiscount: lowestPrice.total?.afterDiscount,
      beforeDiscount: lowestPrice.total?.beforeDiscount,
      beforePromotionAndDiscount: lowestPrice.total?.beforePromotionAndDiscount,
    })
  );
  const beforeDiscount = getFormattedPrice(lowestPrice.total?.beforeDiscount);
  const afterDiscount = getFormattedPrice(lowestPrice.total?.afterDiscount);
  return { afterDiscount, beforeDiscount, beforePromotionAndDiscount };
}

function getFormattedPrice(price: string | undefined): string {
  if (!price) {
    return '';
  }
  return roundPrice(Number.parseFloat(price));
}

export function filterOnlyEligiblePassengers(
  passengers: PassengerI[],
  shopResult: GetShopExcursionMyMSCResponse | OBSI | null
): PassengerI[] {
  if (!shopResult) return passengers;

  return passengers.filter((passenger) =>
    shopResult.prices?.perPassenger.some((px) => px.id === passenger.id && px.isApplicable)
  );
}
